import React, { useState, useEffect, useCallback, useMemo } from "react";
import API_BASE_URL from "../RestAPI.js";
import { showErrorToast } from "../../constants/ToastService.js";
import "../SharedTable.css";
import FilterDropdown from "../Common Components/FilterDropdown";
import useCurrentProject, { resolveProject } from "../Common Components/useCurrentProject";

const PAGE_SIZE = 15;

const Reports = () => {
    const [reportsData, setReportData] = useState([]);
    const [projects, setProjects] = useState([]);
    const [currentProject, setCurrentProject] = useCurrentProject();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const getProjects = useCallback(() => {
        fetch(`${API_BASE_URL}core/project/`, { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } })
            .then(res => res.json()).then(data => { if (data.status === "success") { setProjects(data.data); setCurrentProject(prev => resolveProject(data.data, prev)); } else showErrorToast(data.message); })
            .catch(err => { console.error(err); showErrorToast("Failed to fetch projects"); });
    }, []);

    const getReportData = useCallback(async (projectId) => {
        try {
            const res = await fetch(`${API_BASE_URL}reports/requirement/tat/report/?from_date=&to_date=&project_id=${projectId}`, { method: "GET", headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } });
            const data = await res.json();
            if (data.status === "success") setReportData(data.data); else showErrorToast(data.message);
        } catch (err) { console.error(err); showErrorToast("Failed to fetch report"); }
    }, []);

    useEffect(() => { if (currentProject) getReportData(currentProject); else setReportData([]); }, [currentProject]);
    useEffect(() => { getProjects(); }, [getProjects]);

    const filtered = useMemo(() => {
        if (!search.trim()) return reportsData;
        const q = search.toLowerCase();
        return reportsData.filter(r => (r.requirement_code || '').toLowerCase().includes(q) || (r.requirement || '').toLowerCase().includes(q) || (r.cre_name || '').toLowerCase().includes(q) || (r.ba_name || '').toLowerCase().includes(q));
    }, [reportsData, search]);

    const paginated = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

    return (
        <div style={{ padding: '20px 28px' }}>
            <div className="fd-selector-row">
                <FilterDropdown
                    label="Project"
                    value={currentProject}
                    options={projects || []}
                    onChange={setCurrentProject}
                    renderLabel={o => o?.project_name || ''}
                    getValue={o => String(o?.id)}
                />
            </div>
            <div className="fd-card">
                <div className="fd-card-header">
                    <div className="fd-card-header-left">
                        <h1 className="fd-card-title">Client TAT Report</h1>
                        <p className="fd-card-subtitle">Track turnaround times for CRE and BA across requirements</p>
                    </div>
                </div>
                <div className="fd-toolbar">
                    <input className="fd-search" type="text" placeholder="Search requirements..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                    <span className="fd-record-count">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
                </div>
                <table className="fd-table">
                    <thead><tr><th>Req. No</th><th>Requirement</th><th>CRE</th><th>BA</th><th>CRE TAT</th><th>BA TAT</th><th>Overall TAT</th></tr></thead>
                    <tbody>
                        {paginated.length > 0 ? paginated.map((r, i) => (
                            <tr key={r.id || i}>
                                <td>{r.requirement_code || '-'}</td>
                                <td>{r.requirement || '-'}</td>
                                <td>{r.cre_name || '-'}</td>
                                <td>{r.ba_name || '-'}</td>
                                <td>{r.cre_tat || '-'}</td>
                                <td>{r.ba_tat || '-'}</td>
                                <td>{r.overall_tat || '-'}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan={7} className="fd-empty">{search ? `No results for "${search}"` : 'No requirements available'}</td></tr>
                        )}
                    </tbody>
                </table>
                {totalPages > 1 && (
                    <div className="fd-pagination">
                        <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                        <div className="fd-pagination-controls">
                            <button className="fd-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1).map(p => (
                                <button key={p} className={`fd-page-btn ${p === page ? 'fd-page-btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                            ))}
                            <button className="fd-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Reports;
